import { Control, Controller, ControllerProps, FieldValues, Path } from 'react-hook-form';
import { TextField } from '@mui/material';
import MuiAutocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { useAutocompletePopperStyles, useAutocompleteStyles } from './autocomplete.styles';
import { Close } from '@mui/icons-material';

export interface AutocompleteProps<T extends FieldValues = FieldValues> {
  // Generic over the form's values: from react-hook-form 7.90 a typed `Control` no longer widens to `Control<any>`.
  control: Control<T>;
  name: Path<T>;
  rules?: ControllerProps<T, Path<T>>['rules'];
  defaultValue?: any;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  required?: string | boolean;
  placeholder?: string;
  fullWidth?: boolean;
  helperText?: string;
  inputStyles?: React.CSSProperties;
  options: any[];
  groupBy?: (option: any) => string;
  getOptionLabel?: (option: any) => any;
  getOptionSelected?: (option: any, value: any) => boolean;
  renderOption?: (option: any, state: AutocompleteRenderOptionState) => ReactNode;
  renderTags?: (value: any[], getItemProps: (args?: { index?: number }) => any) => ReactNode;
  // Material UI v9 moved TextField's InputProps to slotProps.input.
  InputProps?: NonNullable<ComponentProps<typeof TextField>['slotProps']>['input'];
  disableClearable?: boolean;
}

export const Autocomplete = <T extends FieldValues>({
  control,
  name,
  rules,
  label = '',
  loading = false,
  disabled = false,
  required = false,
  placeholder = '',
  fullWidth = false,
  helperText = '',
  inputStyles,
  options,
  groupBy,
  getOptionLabel,
  getOptionSelected,
  renderOption,
  renderTags,
  InputProps,
  disableClearable = false
}: AutocompleteProps<T>) => {
  const classes = useAutocompleteStyles({ disableClearable });
  const popperClasses = useAutocompletePopperStyles();
  const [inputValue, setInputValue] = useState('');
  const [inputFilterValue, setInputFilterValue] = useState('');

  // If the form value is changed programatically (e.g. from a parent component), update the input value
  const currentValue = control._getWatch(name);

  useEffect(() => {
    if (currentValue && !inputValue) {
      const selected = getOptionSelected
        ? options.find((option) => getOptionSelected(option, currentValue))
        : options.find((option) => option.value === currentValue);
      if (selected) {
        setInputValue(selected.label);
      }
    }
    if (!currentValue && !inputFilterValue) {
      setInputValue('');
    }
  }, [currentValue, getOptionSelected, inputFilterValue, inputValue, options]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required, ...rules }}
      render={({ field: { onChange, value, ...rest }, fieldState: { error: fieldError } }) => {
        return (
          <MuiAutocomplete
            classes={{
              inputRoot: classes.inputRoot,
              popper: popperClasses.popper
            }}
            {...rest}
            // The form holds the option's `value`, not the option, so MUI can't infer a value type from it.
            value={value as any}
            id={name}
            loading={loading}
            disabled={disabled}
            disableClearable={disableClearable}
            options={options}
            openOnFocus
            fullWidth={fullWidth}
            onChange={(_e, data) => {
              const option = data as any;
              setInputValue(option ? option.label : '');
              setInputFilterValue('');
              onChange({ target: { value: option ? option.value : '' } });
            }}
            inputValue={inputFilterValue}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={getOptionSelected}
            renderOption={
              renderOption
                ? (props, option, state) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key} {...optionProps}>
                        {renderOption(option, state)}
                      </li>
                    );
                  }
                : undefined
            }
            renderValue={renderTags}
            groupBy={groupBy}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  style={inputStyles}
                  placeholder={placeholder}
                  required={!!required}
                  error={!!fieldError}
                  label={label}
                  helperText={fieldError ? fieldError.message : helperText}
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      ...params.slotProps.input,
                      ...InputProps,
                      ...((InputProps as any)?.endAdornment &&
                        value && {
                          endAdornment: (
                            <div className={classes.endAdornment}>
                              <Close
                                className={classes.endAdornmentClearButton}
                                onClick={(event) => {
                                  event.preventDefault();
                                  setInputValue('');
                                  onChange({ target: { value: '' } });
                                }}
                              />
                              {(InputProps as any).endAdornment}
                            </div>
                          )
                        })
                    },

                    htmlInput: {
                      ...params.slotProps.htmlInput,
                      value: inputValue,
                      onChange: (event) => {
                        event.preventDefault();
                        setInputValue(event.currentTarget.value);
                        setInputFilterValue(event.currentTarget.value);
                        onChange({ target: { value: '' } });
                      }
                    }
                  }}
                />
              );
            }}
          />
        );
      }}
    />
  );
};
